import {useRef, useState} from "react";
import {Alert, Button, message, Modal, Space} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  PlusCircleFilled,
} from "@ant-design/icons";
import {
  ProductContent,
  useDigitalProductContentList,
  useDigitalProductContentMutations,
} from "../../../../features/digital-products";
import {FullPageLoader} from "../../../../components/full-page-loader";
import {OfferingFormAccordionItem} from "../../../../components/offering-form/accordion/accordion-item";
import {
  StyledProductContentUploadContentListItem,
  StyledProductContentUploadContentListWrapper,
} from "../../../../components/offering-form/products/content-upload/style";
import {ProductRenameContentModalContent} from "../../../../components/offering-form/products/content-upload/content-list/rename-content/modal";
import {StyledModalContentWrapper} from "../../../../components/calls/upcoming-calls/call-card/call-card.style";

const MAX_FILE_SIZE_MB = 100;
const MAX_FILES = 5;
const ALLOWED_EXTENSIONS = ["docx", "pdf", "csv", "xlsx", "xls", "zip"];

function isAllowedFile(file: File): boolean {
  const fileType = file.type.split("/")[0]?.toLowerCase();
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return (
    fileType === "video" || fileType === "audio" || ALLOWED_EXTENSIONS.includes(extension)
  );
}

/**
 * v2 content panel for a digital product.
 *
 * A product can hold up to {@link MAX_FILES} downloadable files, so this renders
 * the current files (each with rename/delete) plus an add button until the cap
 * is reached. Reads/writes through the digital-products slice; rename stays on
 * the v1 hook (reused modal), which the slice's content query refetches on ok.
 */
export function EditProductContentPanel({productId}: {productId: number}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [renameTarget, setRenameTarget] = useState<ProductContent | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const contentQuery = useDigitalProductContentList(productId);
  const {uploadContent, isUploadingContent, deleteContent, isDeletingContent} =
    useDigitalProductContentMutations(productId);

  const contents = contentQuery.data ?? [];
  const atCap = contents.length >= MAX_FILES;
  // A product must keep at least one content file, so the last one can't be
  // deleted (add another first).
  const canDelete = contents.length > 1;

  function handleFileInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      message.error("Max file size is 100MB");
      return;
    }
    if (!isAllowedFile(file)) {
      message.error("File not allowed! Only video, audio and Ebook formats are allowed");
      return;
    }
    uploadContent(
      {file},
      {
        onSuccess: () => message.success("Your content has been uploaded successfully!"),
        onError: (error) =>
          message.error(
            error.response?.data?.message || error.message || "Failed to upload content"
          ),
        onSettled: () => {
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        },
      }
    );
  }

  function handleDelete(content: ProductContent) {
    setDeletingId(content.id);
    deleteContent(
      {contentId: content.id},
      {
        onSuccess: () =>
          message.success("The selected content has been deleted successfully!"),
        onError: (error) =>
          message.error(
            error.response?.data?.message || error.message || "Failed to delete content"
          ),
        onSettled: () => setDeletingId(null),
      }
    );
  }

  function handleRenameModalOk() {
    setRenameTarget(null);
    contentQuery.refetch();
  }

  return (
    <Space direction="vertical" size={16} style={{display: "flex"}}>
      <OfferingFormAccordionItem key="Content upload" index={0} title="Content upload">
        {contentQuery.isLoading ? (
          <FullPageLoader />
        ) : contentQuery.isError ? (
          <Alert
            message="Error"
            description={contentQuery.error?.message}
            type="error"
            showIcon
          />
        ) : (
          <StyledProductContentUploadContentListWrapper>
            <Space size={16} direction="vertical" style={{display: "flex"}}>
              <p className="product-content-list-title">
                You can add up to {MAX_FILES} files. PDF, Videos, Audio, Docx up to 100MB
                of size.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileInputChange}
                style={{display: "none"}}
              />
              {contents.length <= 1 && (
                <Alert
                  type="info"
                  showIcon
                  message={
                    contents.length === 0
                      ? "Upload at least one content file."
                      : "At least one content file is required. Add another file to remove this one."
                  }
                />
              )}
              {contents.length > 0 && (
                <Space size={16} direction="vertical" style={{display: "flex"}}>
                  {contents.map((content) => (
                    <StyledProductContentUploadContentListItem key={content.id}>
                      <div className="product-content-list-item-info">
                        <div className="product-content-list-item-name-wrapper">
                          <FileOutlined className="product-content-list-item-file-icon" />{" "}
                          <p className="product-content-list-item-name">
                            {`${content.fileName} · ${content.humanReadableFileSize}`}
                          </p>
                        </div>
                        <div>
                          <Space size={16}>
                            <Button
                              type="text"
                              shape="circle"
                              icon={<EditOutlined />}
                              title="Edit content"
                              size="small"
                              onClick={() => setRenameTarget(content)}
                            />
                            <Button
                              type="text"
                              shape="circle"
                              icon={<DeleteOutlined />}
                              title={
                                canDelete
                                  ? "Delete content"
                                  : "At least one content file is required"
                              }
                              size="small"
                              disabled={!canDelete}
                              loading={isDeletingContent && deletingId === content.id}
                              onClick={() => handleDelete(content)}
                            />
                          </Space>
                        </div>
                      </div>
                    </StyledProductContentUploadContentListItem>
                  ))}
                </Space>
              )}
              {!atCap && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    size="small"
                    icon={<PlusCircleFilled />}
                    loading={isUploadingContent}
                  >
                    Add content
                  </Button>
                </div>
              )}
            </Space>
          </StyledProductContentUploadContentListWrapper>
        )}
      </OfferingFormAccordionItem>
      {renameTarget && (
        <Modal
          title="Rename file"
          footer={null}
          visible={!!renameTarget}
          onOk={handleRenameModalOk}
          onCancel={() => setRenameTarget(null)}
          maskClosable={false}
        >
          <StyledModalContentWrapper>
            <ProductRenameContentModalContent
              renameModalDetails={{
                fileName: renameTarget.fileName,
                fileExtension: renameTarget.fileType,
                contentId: renameTarget.id,
                productId,
              }}
              handleRenameContentModalOk={handleRenameModalOk}
              handleRenameContentModalCancel={() => setRenameTarget(null)}
            />
          </StyledModalContentWrapper>
        </Modal>
      )}
    </Space>
  );
}
